import { useState, type SyntheticEvent } from "react";

import { resolveCrop, type BoundingArea, type Natural } from "@/lib/image-crop";
import { cn } from "@/lib/utils";

type Props = {
  src: string;
  alt: string;
  /** The capturing device's crop box, or null when it crops on-device (POS2-1105). */
  box: BoundingArea | null;
  /** Fill the surrounding tile (the feed thumbnails, which set their own aspect
   * ratio) instead of taking the box's shape. */
  fill?: boolean;
  className?: string;
  /** Composed after the crop's own placement — the lightbox's zoom. */
  transform?: string;
  transformOrigin?: string;
  loading?: "lazy";
  draggable?: boolean;
  onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onError?: () => void;
};

/**
 * A capture, cropped to its device's bounding box.
 *
 * The crop is presentation-only: no second request, no cropped copy, the R2
 * original untouched — the same contract classification keeps (POS2-957).
 *
 * Nothing here is in pixels, so one implementation holds from a 180px feed
 * thumbnail to a full-screen lightbox. The image is scaled until the box covers
 * the frame — `max()` of the two container-relative candidates, which is what
 * `object-fit: cover` does, only about the box rather than the whole photo — and
 * then shifted until the box's centre is the frame's centre. Where the frame
 * already has the box's shape the two candidates agree and the box lands exactly
 * on its edges.
 */
export function CroppedImage({
  src,
  alt,
  box,
  fill,
  className,
  transform,
  transformOrigin,
  loading,
  draggable,
  onLoad,
  onError,
}: Props) {
  // The box is in the original's pixel space, so neither the placement nor the
  // out-of-bounds rule can be settled until the image reports its natural size.
  // Held with its src so a changed image never renders against a stale measurement.
  const [measured, setMeasured] = useState<(Natural & { src: string }) | null>(null);
  const natural = measured?.src === src ? measured : null;

  function handleLoad(event: SyntheticEvent<HTMLImageElement>) {
    const img = event.currentTarget;
    setMeasured({ src, width: img.naturalWidth, height: img.naturalHeight });
    onLoad?.(event);
  }

  const shared = { src, loading, draggable, onLoad: handleLoad, onError };

  // No box, or a box the image turned out to be too small for: today's markup.
  if (!box || (natural && !resolveCrop(box, natural))) {
    return (
      <img {...shared} alt={alt} className={className} style={{ transform, transformOrigin }} />
    );
  }

  const [x, y, w, h] = box;
  // Percentages on `transform` resolve against the image's own box, which is what
  // puts the *box's* centre on the frame's centre rather than the photo's.
  // `height` is set inline rather than by a class because the surfaces pass their
  // own layout classes through for the uncropped path — `size-full` among them,
  // whose `h-full` would otherwise squash the image out of its aspect ratio.
  const placement = natural
    ? {
        width: `max(${(natural.width / w) * 100}cqw, ${(natural.width / h) * 100}cqh)`,
        height: "auto",
        transform: `translate(${-((x + w / 2) / natural.width) * 100}%, ${
          -((y + h / 2) / natural.height) * 100
        }%) ${transform ?? ""}`.trim(),
      }
    : { height: "auto", transform };

  return (
    <div
      className={cn("overflow-hidden", fill ? "absolute inset-0" : "relative w-full")}
      // `size` rather than `inline-size`: the cover scale needs the frame's height
      // as well as its width, and both are known — the tile fixes its own aspect
      // ratio, and the shaped frame takes the box's.
      style={{ containerType: "size", aspectRatio: fill ? undefined : `${w} / ${h}` }}
      data-testid="crop-frame"
    >
      <img
        {...shared}
        alt={alt}
        // Hidden until measured: the frame is already the right shape, so this is
        // the difference between the crop appearing and the whole kitchen flashing
        // up first and snapping into it a moment later.
        className={cn(
          "absolute left-1/2 top-1/2 h-auto max-w-none",
          className,
          !natural && "invisible",
        )}
        style={{ ...placement, transformOrigin }}
      />
    </div>
  );
}
